import { Box, useTheme } from '@mui/material';
import {
  Container,
  FormContainer,
  HeaderContainer,
  InfoCarouselContainer,
  StyledBackgroundImage,
  StyledLogo,
} from './SubscriptionBase.styles';
import Background from '@assets/backgrounds/questionnaire-intro-background.png';
import Logo from '@assets/logos/swise_white_on_dark.svg';
import { FormattedMessage } from 'react-intl';
import { IconButton, ProgressRing, Text } from '@components';
import CancelIcon from '@mui/icons-material/Cancel';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { Steps } from '@pages/Investment/Progress/Progress.types';
import { useUserQuery } from '@features/api/userSlice';
import { TOTAL_STEPS } from '@pages/Investment/Investment.consts';

const SubscriptionBase = (): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const { productId } = useParams();

  const {
    data: user,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
  } = useUserQuery();

  const completedSteps = (state?.completedSteps as Steps[]) ?? [Steps.None];

  useEffect(() => {
    return () => localStorage.removeItem('companyDetails');
  }, []);

  useEffect(() => {
    if (
      !state?.progress &&
      state?.progress !== 0 &&
      pathname.includes('/investment-subscription')
    ) {
      navigate('/');
    }
  }, [state?.progress, navigate, pathname]);
  /* eslint-disable */
  useEffect(() => {
    if (isFetchingUser || isLoadingUser) return;

    if (user?.taxDetails?.usTaxPayer && !pathname.includes('/tax-details')) {
      navigate(`/investment-subscription/${productId}/unavailable`, {
        preventScrollReset: false,
        state: {
          progress: 4,
          type: 'us_tax_person',
          previousPathName: 'tax-details',
        },
        replace: true,
      });
    } else if (
      user?.bankDetails?.soleAccountOwner !== undefined &&
      !user.bankDetails?.soleAccountOwner &&
      !pathname.includes('/bank-details')
    ) {
      navigate(`/investment-subscription/${productId}/unavailable`, {
        state: {
          progress: 6,
          type: 'nominee',
          previousPathName: 'bank-details',
        },
        replace: true,
      });
    }
  }, [isFetchingUser, isLoadingUser, productId, user]);
  /* eslint-enable */

  return (
    <Container>
      <InfoCarouselContainer>
        <StyledBackgroundImage alt='background' src={Background} />
        <Box>
          <StyledLogo src={Logo} />
          <Text
            variant='h2'
            fontWeight='fontWeightSemiBold'
            sx={{ color: theme.palette.common.white }}
          >
            <FormattedMessage id='misc.completeProfile' />
          </Text>
        </Box>
        <ProgressRing value={state?.progress} maxValue={TOTAL_STEPS} />
        <Text
          variant='h6'
          fontWeight='fontWeightRegular'
          sx={{ color: theme.palette.primary[700] }}
        >
          <FormattedMessage id='questionnaire.questionnaireTip' />
        </Text>
      </InfoCarouselContainer>
      <FormContainer hasFooter={!pathname.includes('/contract-signature')}>
        <HeaderContainer>
          <Text
            variant='h5'
            fontWeight='fontWeightBold'
            color={theme.palette.common.white}
            sx={{ display: { md: 'none' } }}
          >
            <FormattedMessage id='misc.completeProfile' />
          </Text>
          <IconButton
            data-testid='close-button'
            size='small'
            iconBoxHeight={25}
            iconBoxWidth={25}
            icon={() => <CancelIcon color='info' />}
            onClick={() =>
              completedSteps.includes(Steps.ContractSignature)
                ? navigate('/portfolio/transactions')
                : navigate('/', {
                    replace: true,
                  })
            }
          />
        </HeaderContainer>
        <Outlet />
      </FormContainer>
    </Container>
  );
};

export default SubscriptionBase;
